import {
  AbstractNotificationsService,
  NotificationItemView,
  NotificationModel,
  NotificationRealtimeService,
} from '@aionzdev/modules';
import { HttpResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, defer, finalize, map, takeUntil, tap, throwError } from 'rxjs';
import { AlertNotification, NotificationsApiService } from './api/notifications-api.service';
import { NotificationsMapperService } from './notifications-mapper.service';
import { acceptsNotification } from './notification-domains';
import { AuthModuleService } from './auth-module.service';

@Injectable({ providedIn: 'root' })
export class NotificationsService extends AbstractNotificationsService {
  private readonly notificationsApi = inject(NotificationsApiService);
  private readonly notificationRealtime = inject(NotificationRealtimeService);
  private readonly notificationsMapper = inject(NotificationsMapperService);
  private readonly authService = inject(AuthModuleService);

  private readonly _totalNotOpen = signal(0);
  readonly totalNotOpen$ = toObservable(this._totalNotOpen);

  private readonly _notificationCreated$ = new Subject<NotificationModel>();
  readonly notificationCreated$ = this._notificationCreated$.asObservable();

  private readonly alerts = signal<AlertNotification[]>([]);
  private readonly receivedAt = signal(Date.now());
  readonly notificationsList = computed<NotificationItemView[]>(() =>
    this.alerts().map(alert => this.notificationsMapper.toView(alert, this.receivedAt()))
  );

  readonly page = signal(0);
  readonly total = signal(0);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly loadError = signal(false);
  private readonly consumed = signal(0);
  readonly hasMore = computed(() => this.page() > 0 && this.consumed() < this.total());
  private readonly cancelFetch$ = new Subject<void>();
  private generation = 0;
  private realtimeRevision = 0;
  private readonly realtimeVersions = new Map<string, number>();
  private readonly readIds = new Set<string>();

  findAlert(id: string): AlertNotification | undefined {
    return this.alerts().find(alert => alert._id === id);
  }

  constructor() {
    super();
    this._totalNotOpen.set(0);
    this.notificationRealtime.setEventName('notification:created');
    this.notificationRealtime.register(this, (data) => {
      const alert = data as AlertNotification;
      this.handleIncomingRealtimeAlert(alert);
      return this.notificationsMapper.map(alert);
    }, acceptsNotification);
    this.authService.sessionCleared$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.resetSession());
  }

  cancelLoading(): void {
    this.cancelFetch$.next();
    this.generation++;
    this.loading.set(false);
    this.loadingMore.set(false);
  }

  resetSession(): void {
    this.cancelLoading();
    this._totalNotOpen.set(0);
    this.alerts.set([]);
    this.page.set(0);
    this.total.set(0);
    this.consumed.set(0);
    this.loadError.set(false);
    this.realtimeVersions.clear();
    this.readIds.clear();
  }

  fetchNotificationsList(): Observable<NotificationItemView[]> {
    return defer(() => {
      this.cancelLoading();
      this.page.set(0);
      this.total.set(0);
      this.consumed.set(0);
      this.loadError.set(false);
      return this.fetchPage(1);
    });
  }

  fetchNextPage(retry = false): Observable<NotificationItemView[]> {
    return defer(() => {
      if (this.loading() || this.loadingMore() || (!retry && this.loadError())
        || (this.page() > 0 && !this.hasMore())) return EMPTY;
      return this.fetchPage(this.page() + 1);
    });
  }

  private fetchPage(page: number): Observable<NotificationItemView[]> {
    const generation = this.generation;
    const revision = this.realtimeRevision;
    this.loadError.set(false);
    (page === 1 ? this.loading : this.loadingMore).set(true);
    return this.notificationsApi.findAll(page, 20).pipe(
      takeUntil(this.cancelFetch$),
      takeUntil(this.authService.sessionCleared$),
      map(res => {
        const body = res.body ?? [];
        const current = this.alerts();
        const retained = page === 1
          ? current.filter(alert => (this.realtimeVersions.get(alert._id) ?? 0) > revision)
          : current;
        const merged = new Map(retained.map(alert => [alert._id, alert]));
        for (const alert of body) {
          if (!acceptsNotification(alert)) continue;
          // Preserve events received while HTTP was in flight and successful local reads.
          if ((this.realtimeVersions.get(alert._id) ?? 0) > revision && merged.has(alert._id)) continue;
          merged.set(alert._id, this.readIds.has(alert._id)
            ? { ...alert, isOpen: true, highlight: false } : alert);
        }
        this.alerts.set([...merged.values()]);
        this.receivedAt.set(Date.now());
        this.page.set(page);
        this.consumed.update(count => count + body.length);
        const header = res.headers.get('X-Total-Count');
        const total = header === null ? NaN : Number(header);
        this.total.set(Number.isSafeInteger(total) && total >= 0
          ? total : this.consumed() + (body.length === 20 ? 1 : 0));
        if (body.length === 0) this.total.set(this.consumed());
        this.updateUnreadCount();
        return this.notificationsList();
      }),
      catchError((error: unknown) => {
        this.loadError.set(true);
        return throwError(() => error);
      }),
      finalize(() => {
        if (generation === this.generation) {
          this.loading.set(false);
          this.loadingMore.set(false);
        }
      }),
    );
  }

  markNotificationAsRead(alertId: string): Observable<unknown> {
    return this.notificationsApi.markRead(alertId).pipe(
      takeUntil(this.authService.sessionCleared$),
      tap(() => {
        this.readIds.add(alertId);
        this.alerts.update(list =>
          list.map(alert => (alert._id === alertId ? { ...alert, isOpen: true, highlight: false } : alert))
        );
        this.updateUnreadCount();
      })
    );
  }

  markAllNotificationsAsRead(): Observable<unknown> {
    // Only the alerts present when the request starts belong to this operation.
    const ids = new Set(this.alerts().map(alert => alert._id));
    return this.notificationsApi.markReadAll().pipe(
      takeUntil(this.authService.sessionCleared$),
      tap(() => {
        ids.forEach(id => this.readIds.add(id));
        this.alerts.update(list =>
          list.map(alert => ids.has(alert._id) ? { ...alert, isOpen: true, highlight: false } : alert)
        );
        this.updateUnreadCount();
      })
    );
  }

  private updateUnreadCount(): void {
    this.setTotalNotOpen(this.alerts().filter(alert => !alert.isOpen).length);
  }

  private handleIncomingRealtimeAlert(alert: AlertNotification): void {
    if (!acceptsNotification(alert)) return;
    this.realtimeVersions.set(alert._id, ++this.realtimeRevision);
    const previous = this.findAlert(alert._id);
    const unreadDelta = Number(!alert.isOpen) - Number(previous !== undefined && !previous.isOpen);
    this.receivedAt.set(Date.now());
    this.alerts.update(list => [alert, ...list.filter(item => item._id !== alert._id)]);
    // The legacy counter may be loaded before the drawer's complete alert list.
    this._totalNotOpen.update(total => Math.max(0, total + unreadDelta));
  }

  createAlert(data: {
    title: string;
    type: string;
    description?: string;
    url?: string;
    highlight?: boolean;
    userId: string;
  }): Observable<NotificationModel> {
    return this.notificationsApi.create(data);
  }

  getAllNotifications(props: {
    currentPage: number;
    take: number;
    search?: string;
  }): Observable<HttpResponse<NotificationModel[]>> {
    return this.notificationsApi.findAll(props.currentPage, props.take).pipe(
      takeUntil(this.authService.sessionCleared$),
      map(response => {
        const body = (response.body ?? []).map<NotificationModel>(alert =>
          this.notificationsMapper.map(alert),
        );
        return response.clone({ body });
      }),
      tap(res => {
        if (props.currentPage === 1) this.setTotalNotOpen((res.body ?? []).filter(n => !n.openingDate).length);
      }),
    );
  }

  getOneNotification(_alertId: string): Observable<NotificationModel> {
    throw new Error('Method not implemented.');
  }

  markRead(alertId: string): Observable<NotificationModel> {
    return this.notificationsApi.markRead(alertId);
  }

  markReadAll(): Observable<NotificationModel> {
    return this.notificationsApi.markReadAll();
  }

  getTotalNotOpen(): Observable<number> {
    return this.totalNotOpen$;
  }

  setTotalNotOpen(value: number): void {
    this._totalNotOpen.set(value);
  }

  receiveRealtimeNotification(notification: NotificationModel): void {
    this._notificationCreated$.next(notification);
    if (!this.findAlert(notification.id) && !notification.openingDate) {
      this._totalNotOpen.update(total => total + 1);
    }
  }
}
